import { IVec2 } from "@base/math/Vec2";
import { IVec3 } from "@base/math/Vec3";
import { inferRecord, inverseRecord, PartialRecord, pick, TupleToUnion } from "@base/util/TypeUtil";
import { KederSailMountType, KederSize } from "@biz/definitions/KederDefinitions";
import { PoleMountArticle } from "@biz/definitions/PoleMountDefinitions";
import { INotesAndFiles, IProductData } from "@biz/definitions/ProductDefinitions";
import { PredefinedImageId } from "@biz/items/PredefinedMaterials";
import { PoleAmplifierArticle, PoleSwordArticle } from "@biz/items/sunSail/logic/AmplifierLogic";

import type { Radian } from "@base/math/MathUtil";
import type { IMat4 } from "@base/math/Mat4";
import type { ISize2 } from "@base/math/Size2";
import type { IClothItemData } from "@biz/logic/ClothData";
import type { Material, MeshStandardMaterial, MeshStandardMaterialParameters } from "three";
import type { ISail2dForm, Sail2dCalculationType, SailCoilUpType } from "@biz/models/Sail2dDefinitions";
export type AxleMeasureType = "netto" | "brutto";

export const MAX_POLE_LENGTH = 6;
export type StateSelectionValue<T> = { value: T | undefined; possibleValues: readonly T[], show: boolean }

export const defaultLanguage = "deutsch";

export enum RaycastPriority {
  Gizmo = 1,
  Pole = 2,
  Error = 10
}

export const RopeColors = {
  Default: "#666",
  Selected: "#fffe00",
  Anchor: "#666",
  HeightChange: "#666",
  PoleSword: "#666",
  Raff: "#666", //"#6a7591"
  RevolvingRope: {
    default: "#666", //"#4F5451",
    black: "#333",
    grey: "#888"
  }
}

export const RopeLineWidth = {
  Default: 0.015,
  Selected: 0.035
}

export const kauscheArticles = ["00E13000053", "00E13001415", "00E13000153"] as const;
export type RopeDiameter = 4 | 6.5 | 8 | 10;

export type HeightChangeType = "motor" | "pulley" | "slide" | "winch" | "sandy";

export type PointName = "A" | "B" | "C" | "D" | "E" | "F";
export type PropertyQuickType = "properties" | "quick" | "sail2d" | "sc";

export enum PoleIndex {
  A,
  B,
  C,
  D,
  E,
  F,
}

export enum CenterPoleIndex {
  A = 1000,
  B,
  C,
  D,
}

export type PoleId = -1 | PoleIndex | CenterPoleIndex;

export type AxlePoleWallMountType = "mm" | "mw" | "wm" | "ww";

export const sunSailSides = ["AB", "AC", "AD", "AE", "AF", "BC", "BD", "BE", "BF", "CD", "CE", "CF", "DE", "DF", "EF"] as const;
export const edgeNames = sunSailSides.map((ss) => ss.toLocaleLowerCase() as EdgeName)
export type SunSailSides = TupleToUnion<typeof sunSailSides>;
export type SunSailNetPoints = Lowercase<PointName>;
export type SunSailNetSides = Lowercase<SunSailSides>;
export type EdgeName = SunSailNetSides;

export type PointType = "none" | "pole" | "wall";

export type ClothTextureTypes = "rays" | "linear" | "quad" | "simple" | "simple3" | "curtainDefault" | "curtainChillSail" | "raffDefault" | "940" | "loop" | "hook" | "profi";

export interface ISunSailSide {
  first: PoleIndex;
  second: PoleIndex;
  groupIndices: number[];
  netBorder: boolean;
  axle?: boolean;
}

// don't remove / add inbetween
export enum ClothType {
  Austronet940,
  Soltis92,
  Austrosail_PES,
  Austrosail_Acryl,
  Austrosail_Nano,
  Austrosail_Nano_Protect,
  Austronet92,
  Concept,
  Concept_Stretch,
  Soltis86,
  Austronet950,
  Austronet_Stretch,
  Austrosail_Elastic,
  SoltisW96,
  ChillSail,
  Austrosail_Screen20,
  Austrosail_Screen22,
  Austrosail_Nano_Glued,
  Lotus,
  Austronet932,
  Silvanet,
  Finoo,
  FinooPro,
  Telina,
  Prestar,
  Acrillia,
  Austronet960,
  Count,
  None,
}

export type SunSailTextureType = "simple" | "linear" | "rays";

export enum CornerType {
  D_Ring25 = "D_Ring25",
  D_Ring40 = "D_Ring40",
  BigPlate = "BigPlate",
  Strap25 = "Strap25",
  Strap40 = "Strap40",
  /** @deprecated replaced by OrcaPlate*/
  RoundPlate = "RoundPlate", // depreacted => wird durch OrcaPlate ersetzt
  OrcaPlate = "OrcaPlate",
  ChillSail = "ChillSail",
  /** @deprecated use OrcaRing*/
  O_Ring = "O_Ring", // 3d Segel,
  OrcaRing = "OrcaRing",
  None = "None",
}

export enum DroppoleLengthMode {
  Default = "Default",
  Cut = "Cut",
  _3 = "_3",
  _4 = "_4",
  _5 = "_5",
}

export enum PoleMountType {
  None = "None",
  GroundSleeve = "GroundSleeve",
  EarthScrew = "EarthScrew",
  GroundPlate = "GroundPlate",
  Clamp = "Clamp",
  BaseCube = "BaseCube",
  ConcreteCube = "ConcreteCube",
  Special = "Special"
}

export type MaterialGroup = "special" | "wall" | "alu" | "steel" | "droppole" | "iron48" | "iron89" | "iron114" | "telescope" | "profile";
export type MaterialColor = "standard" | "black" | "wood";

export const materialInfos = {
  "Steel": { group: "steel", color: "standard" },
  "Steel_V4A": { group: "steel", color: "standard" },
  "Alu": { group: "alu", color: "standard" },
  "AluWood": { group: "alu", color: "wood" },
  "Alu_Black": { group: "alu", color: "black" },
  "Droppole": { group: "droppole", color: "standard" },
  "Droppole_Black": { group: "droppole", color: "black" },
  "Telescope": { group: "telescope", color: "standard" },
  "Telescope_Black": { group: "telescope", color: "black" },
  "TelescopeWood": { group: "telescope", color: "wood" },
  "Iron48": { group: "iron48", color: "standard" },
  "Iron114": { group: "iron114", color: "standard" },
  "Iron89": { group: "iron89", color: "standard" },
  "Iron89_Black": { group: "iron89", color: "black" },
  "Profile": { group: "profile", color: "standard" },
  "Profile_Black": { group: "profile", color: "black" },
} satisfies Record<string, { group: MaterialGroup, color: MaterialColor }>;

export type MaterialType = keyof typeof materialInfos;

export const sailMounts = inferRecord()({
  snap: {},
  rope: {},
  direct: {},
  float: {}, // flex
});
export type SailMount = keyof typeof sailMounts

export enum TwistingSetType {
  None,
  Wall,
  WallPole,
  Pole,
}

export enum PoleType {
  None = "None",
  Pole = "Pole",
  Wall = "Wall",
  Frame = "Frame",
  Special = "Special",
  SailOnly = "SailOnly",
  Keder = "Keder",
}

export enum SunSailType {
  SolidayA,
  SolidayM,
  SolidayC,
  SolidayCS,
  SolidayTwin,
  Standard,
  Fixed,
  Concept,
  RaffM,
  SolidayX,
  SolidayXS,
  RaffC,
  Curtain,
  Lounge,
  ReservedSandy, // was Sandy
  ModelProduct,
  StandardCollection,
  SolidayOne,
  SolidayFlex,
  SandyRect,
  SandyPoly,
  Net,
  Count,
  None = -1,
}

export interface ISunSailTypeInfo {
  name: string;
  shortName: SunSailTypeNameShort;
  isSail?: "RS" | "SO";
  isRaff?: boolean;
  isCurtain?: boolean;
  isOutdoor?: boolean;
  isNet?: boolean;
}

export type SunSailTypeNameShort = "MA" | "M" | "C" | "CS" | "Twin" | "Standard" | "Stds" | "Maß" | "3D" | "RM" | "X" | "XS" | "RC" | "One" | "Flex" | "SdyR" | "SdyP" | "Odv" | "Lounge" | "MP" | "Net" | "Unknown";
export const sailTypesMap: Record<SunSailType, ISunSailTypeInfo> = {
  [SunSailType.SolidayC]: { name: "Soliday-C", shortName: "C", isSail: "RS" },
  [SunSailType.SolidayCS]: { name: "Soliday-CS", shortName: "CS", isSail: "RS" },
  [SunSailType.SolidayTwin]: { name: "Soliday-Twin", shortName: "Twin", isSail: "RS" },
  [SunSailType.SolidayM]: { name: "Soliday-M", shortName: "M", isSail: "RS" },
  [SunSailType.SolidayA]: { name: "Soliday-MA", shortName: "MA", isSail: "RS" },
  [SunSailType.SolidayX]: { name: "Soliday-X", shortName: "X", isSail: "RS" },
  [SunSailType.SolidayXS]: { name: "Soliday-XS", shortName: "XS", isSail: "RS" },
  [SunSailType.SolidayOne]: { name: "Soliday-ONE", shortName: "One", isSail: "RS" },
  [SunSailType.SolidayFlex]: { name: "Soliday-FLEX", shortName: "Flex", isSail: "RS" },
  [SunSailType.Standard]: { name: "Soliday Standard", shortName: "Standard", isSail: "SO" },
  [SunSailType.Fixed]: { name: "Sonnensegel auf Maß", shortName: "Maß", isSail: "SO" },
  [SunSailType.Concept]: { name: "3D Segel", shortName: "3D", isSail: "SO" },
  [SunSailType.ReservedSandy]: { name: "Sandy System Old", shortName: "SdyP", isSail: "SO" },
  [SunSailType.SandyRect]: { name: "Sandy Rechteck", shortName: "SdyR", isSail: "SO" },
  [SunSailType.SandyPoly]: { name: "Sandy Poly", shortName: "SdyP", isSail: "SO" },
  [SunSailType.StandardCollection]: { name: "Standard-Liste", shortName: "Stds", },
  [SunSailType.Net]: { name: "Schutznetz", shortName: "Net", isNet: true },

  [SunSailType.RaffM]: { name: "Raff-M", shortName: "RM", isRaff: true },
  [SunSailType.RaffC]: { name: "Raff-C", shortName: "RC", isRaff: true },
  [SunSailType.Curtain]: { name: "Outdoor Vorhang", shortName: "Odv", isCurtain: true },
  [SunSailType.Lounge]: { name: "Lounge", shortName: "Lounge", isOutdoor: true },
  [SunSailType.ModelProduct]: { name: "Modell", shortName: "MP", isOutdoor: true },
  [SunSailType.Count]: { name: "Count", shortName: "Unknown" },
  [SunSailType.None]: { name: "None", shortName: "Unknown" },
}

export const sailTypeByShortName = inverseRecord(sailTypesMap, (item) => item.shortName)

export type SunSailShapeType = "tri" | "triA" | "triATri" | "quadA" | "penta" | "hexa" | "quadATri" | "quadAQuad" | "quad" | "sandy";

export type CSPositioning = "rigid" | "flexible";
export type AxleColor = "metal" | "black";
/** @deprecated use AxleType instead */
export type SystemAxleVersionOld = "pre2022" | "2022";

export type WaterDirection = "-" | PointName | SunSailSides;

export type ConcavityType = "none" | "straight" | "concave";

export interface IDistIn {
  type: "in";
  val: Auto<number | number[]>;
}

export interface IDistOut {
  type: "out";
  val: Auto<number>;
}

export type IDistInOut = IDistIn | IDistOut;

export type LengthBType = number | { enabled: boolean; val: number; }

export interface ISlot<T = any> {
  articleId: string;
  info?: T;
}

export const pylonTypes = ["ZT1000", "ZT700", "ZT500"] as const;
export type PylonType = TupleToUnion<typeof pylonTypes>;

export type Auto<T> = T | "auto";

export interface IRopeArticle {
  articleId?: string;
  length?: number;
}
export interface IRopeArticleWithAuto extends IRopeArticle {
  auto: boolean;
}

export interface IRevolvingRopeInfo {
  id: string;
  length: number;
  color: "grey" | "black"
}

export interface IPoleDataRopes {
  sailRope?: IRopeArticle;
  sandyOperatingRope?: IRopeArticle;
}

export type WinchHeightChangePriority = "winch" | "heightChange";
// new 3d configurator
export interface IPoleData extends IPoleDataRopes {
  poleType: PoleType;
  position: IVec3; // x, niveau, z
  halfVec?: IVec3; // sandy precalculated halfVec
  material: MaterialType;
  // articleSlots: PartialRecord<"primaryMount" | "wallMount" | "poleMountClamp" | "earthScrew" | "coilUp" | "heightChange" | "sailMount" | "sailMountWinch" | "sailPulley" | "poleSword" | "amplifier" /* | "corner"*/, string>,
  articleSlots: {
    primaryMount?: string;
    twistingSetMount?: string;
    wallMount?: string;
    poleMountClamp?: string;
    earthScrew?: PoleMountArticle;
    coilUp?: string;
    heightChange?: string;
    heightChangeMotorCable?: string;
    sailMount?: string;
    sailMountWinch?: string;
    sailPulley?: string;
    poleSword?: PoleSwordArticle;
    amplifier?: PoleAmplifierArticle;
    amplifierRopeLength?: number;
    operatingRopeKlampe?: string;
    corner?: string;
  },
  cornerType?: CornerType;
  mount: PoleMountType;
  specialConsole?: ISpecialConsoleData;
  angle: number;
  length: number;
  lengthB?: { enabled: boolean; val: number; },
  // angleB?:
  dist: IDistInOut;
  mountToAxle?: number;
  topMountY?: Auto<number>;
  pylonType?: PylonType;
  coilUpHeight: Auto<number>;
  heightChangeCoilUpHeight: Auto<number>;

  sailMount?: SailMount;
  winchHeightChangePriority: WinchHeightChangePriority;
  sandyOperatingRopePoleId?: PoleId;
  additionalPole?: { distance: number }
}

export type ISpecialConsoleData = INotesAndFiles
export interface ISvgMeasureOptions {
  sailMeasures: boolean;
  distOut: boolean;
  sailMountMeasures: boolean;
  distIn: boolean;
  diagonals: boolean;
  infoOverlay: boolean;
  centerPointTriangulation: boolean;
}


export interface IStandardData {
  type: "standard";
  shape: StandardTemplateShape;
  sizeId: StandardTemplateId;
  center: IVec3;
  rotationY: number;
}

export type PositionMode = "bottom" | "mount" | "net";

export type EdgeSeamType =
  "cut" // nur geschnitten
  | "pe" // Randverstärkung
  | "peGrommet" // Ösen
  | "belt" // Profirandverstärkung
  | "beltGrommet" // Gurtband mit Ösen
  | "beltStrap" // Gurtband mit Schlaufe
  | "beltStrapDesign" // Gurtband mit Designring
  | "hemstitch" // Hohlsaum
  | "keder" // Keder
  | "velcro" // Klettverschluss
  | "chillsail" // Bändchen eingenäht?
// | "final" // Endelnaht
// | "small" // schmal gesäumt

export type EdgeMountItemType = "grommet" | "strap" | "strapDesign" | "dringOld"
export type GrommetType = "zinc" | "stainless"
export type EdgePositioningMode = "corners" | "30" | "50" | "userDist" | "userCount";
export interface IEdgeData {
  concavity?: ConcavityType;

  kederSelectionPriority?: number;
  kederSize?: KederSize;
  kederRail?: string;
  kederSailMountType?: KederSailMountType;

  seamType?: EdgeSeamType;

  grommetType?: GrommetType;
  edgePositioning?: EdgePositioningMode;
  edgePositioningUserDist?: number;
  edgePositioningUserCount?: number;
}

export interface IPatchDataRopes {
  sandyOperatingRope?: IRopeArticle;
}
export interface IPatchData extends IPatchDataRopes {
  articleId: "00E13000767";
  poleIds: PoleId[];
  // color, shapes, sizes?
}

export type SailBordersType = "orca" | "individual";
// export type SailEdgeMount = /*"grommet" |*/ "flap" | "designRing";

export interface ISunSailRopes {
  operatingRope?: IRopeArticle;
}

export type PolePositionType = "corner" | "center";

export interface ISunSailData extends IProductData, IClothItemData, ISunSailRopes {
  itemType: "sunSail";
  axleType?: SystemAxleVersionOld | AxleType;
  axleColor?: AxleColor;
  sunSailType: SunSailType;
  sailBordersType?: SailBordersType
  clothCount?: number;
  poles: IPoleData[];
  centerPoles?: IPoleData[]; // e.g. Sandy
  articleSlots: PartialRecord<"revolvingRope" | "rope2" | "motor" | "motorCable", string>;
  twistingSet?: boolean;
  polyPositioning?: boolean[];
  motorPosition?: PoleIndex;
  coilUpDeflectionPosition?: PoleIndex;
  specific?: IStandardData;
  alignDistOut?: boolean;
  form?: { // some additional info for the sunsail order form
    highestPoint?: PoleId;
    coilUpType?: SailCoilUpType;
    twistingSetType?: TwistingSetType;
    calculationType?: Sail2dCalculationType;
    secondAxleDiff?: number;
    // measureOptions?: Partial<ISvgMeasureOptions>;
  },
  positionMode: PositionMode;
  securityCheckOptions?: ISecurityCheckOptions;
  waterDirections?: WaterDirection[];
  // edges
  edges?: PartialRecord<EdgeName, IEdgeData>;
  patches?: IPatchData[];
  obstaclePositions?: PartialRecord<SunSailSides, IVec3>;
  sourceForm?: ISail2dForm;
}

type PredefinedImageSource = {
  type: "predefined";
  id: PredefinedImageId;
}

type UrlImageSource = {
  type: "url";
  url: string;
}

export type UserFileImageSource = {
  type: "userFile";
  fileId: string;
  token: string;
  imageSize?: ISize2;
}
export type ImageSource = PredefinedImageSource | UrlImageSource | UserFileImageSource;

export interface ITexInfoBase {
  image: ImageSource;
  normal?: ImageSource;
}
export interface ITexInfoPattern extends ITexInfoBase {
  type: "pattern";
  rotation: Radian;
  scale: IVec2;
  offset: IVec2;
}

export interface ITexInfoImage extends ITexInfoBase {
  type: "image";
  area: IVec2[];
}

export interface IMaterialSettings {
  textureId?: "wood_white" | "none";
  color?: string;
  roughness?: number;
  metalness?: number;
  normalScale?: number;
  envMapIntensity?: number;
  lightMapIntensity?: number;
  toneMapped?: boolean;
  blend?: "normal" | "overlay" | "alphaComposite";
}

export const materialSettings = inferRecord<IMaterialSettings>()({
  alu: {
    textureId: "none",
    color: "#d7d7d7",
    metalness: 1,
    roughness: 0.58,
    envMapIntensity: 1
  },
  alu_Black: {
    textureId: "none",
    color: "#575e65",
    metalness: 1,
    roughness: 0.58,
    envMapIntensity: 1
  },
  steel: {
    textureId: "none",
    color: "#ffffff",
    metalness: 1,
    roughness: 0.1,
    envMapIntensity: 1
  },
  wood: {
    textureId: "wood_white",
    color: "#dcaf6b",
    metalness: 0,
    roughness: 0.5,
    envMapIntensity: 0.5
  },
  default: {},
  original: {},
  plan: {
    metalness: 0,
    roughness: 1,
    envMapIntensity: 0.1,
    toneMapped: false,
    lightMapIntensity: 0.3,
  },
  luminance: {
    blend: "overlay"
  }
})

export const fenceMaterialSettings = pick(materialSettings, "alu", "alu_Black", "steel", "wood")
export const modelMaterialSettings = pick(materialSettings, "original", "alu", "alu_Black", "steel")
export const groundMaterialSettings = pick(materialSettings, "original", "plan")
export const roofMaterialSettings = pick(materialSettings, "original");
export const openingMaterialSettings = pick(materialSettings, "original");

export type MaterialSettingIds = keyof typeof materialSettings;
export type UserDefinedSettingsId = "userDefined";

export type ITexInfo = ITexInfoImage | ITexInfoPattern;

export type ILineAlign = "left" | "center" | "right";


export const predefinedImageSource: ImageSource = {
  type: "predefined",
  id: "wood",
}

export type StandardTemplateShape = "rect" | "trapezoid" | "triangle";
interface IStandardTemplateBase {
  id: StandardTemplateId;
}

interface IStandardTemplateTriangle extends IStandardTemplateBase {
  shape: "triangle";
  a: number;
  b: number;
  c: number;
}

interface IStandardTemplateRect extends IStandardTemplateBase {
  shape: "rect";
  w: number;
  h: number;
}

interface IStandardTemplateTrapezoid extends IStandardTemplateBase {
  shape: "trapezoid";
  a: number;
  b: number;
  c: number;
  d: number;
}

export type StandardTemplateId = "3X2" | "3X3" | "3X4" | "3X5" | "3X6" | "4X4" | "4X5" | "4X6" | "4X2X3X2" | "4X3X3X3" | "5X3X4X3" | "5X4X4X4" | "6X4X5X4" | "3ECK01" | "3ECK02" | "3ECK03" | "3ECK04" | "3ECK05" | "3ECK06" | "3ECK07" | "3ECK08" | "3ECK09" | "3ECK10" | "3ECK11" | "3ECK12" | "3ECK13" | "3ECK14";
export type IStandardTemplate = IStandardTemplateTriangle | IStandardTemplateRect | IStandardTemplateTrapezoid;

export type IModelOverrideMaterials = ((materials: Record<string, MeshStandardMaterial>) => Record<string, Material>) | MeshStandardMaterialParameters | Record<string, MeshStandardMaterialParameters>;
export interface IModelArticleInfo {
  model: string;
  initialMatrix?: IMat4;
  modelMaterial?: MaterialType;
  dimensions?: IVec3;
  rootNode?: string;
  overrideMaterials?: IModelOverrideMaterials;
  withBounding?: true | "visible";
}

export interface IMotorInfo {
  articleNumber: string;
  remoteType: RemoteType | undefined;
  type: "sunSail" | "raff";
  deprecated?: boolean;
  includedInSystemPrice?: boolean;
}

export const motors: Record<string, IMotorInfo> = {
  "00E13001125": { articleNumber: "00E13001125", remoteType: undefined, type: "raff" },
  "00E13001314": { articleNumber: "00E13001314", remoteType: undefined, type: "sunSail", deprecated: true, includedInSystemPrice: true },
  "00E13001988": { articleNumber: "00E13001988", remoteType: undefined, type: "sunSail", includedInSystemPrice: true },
  // "00E13001882": { articleNumber: "00E13001882", remoteType: undefined, type: "sunSail" }, // https://plaspack.atlassian.net/browse/APP22-722
  "71003A014": { articleNumber: "71003A014", remoteType: "centronic", type: "sunSail", deprecated: true },
  "00E13001883": { articleNumber: "00E13001883", remoteType: "centronic+", type: "sunSail" },
  // "00E13001884": { articleNumber: "00E13001884", remoteType: "centronic+", type: "sunSail" }, // https://plaspack.atlassian.net/browse/APP22-722
}

export interface IRingschraube {
  articleNumber: string;
  drill: string;
}

function u<T>(): { record<C extends { [name: string]: T }>(cfg: C): Record<keyof C, T> } {
  return {
    record: (cfg) => {
      return cfg;
    }
  }
}

export const Ringschrauben = u<IRingschraube>().record({
  M8x60: { articleNumber: "60003A004", drill: "Ø9" }, // nicht vorhanden bis jetzt... https://plaspack.atlassian.net/browse/API-62
  M8x80: { articleNumber: "00E13000066", drill: "Ø9" },
  M8x100: { articleNumber: "00E13000127", drill: "Ø9" },
  M10x110: { articleNumber: "00E13001658", drill: "Ø11" },
  M10x140: { articleNumber: "00E13000833", drill: "Ø11" },
  M8x60_Double: { articleNumber: "60003A005", drill: "Ø9" }, // https://plaspack.atlassian.net/browse/OA-29
  M8x100_Double: { articleNumber: "60003A006", drill: "Ø9" },
})
// get alle articleNumbers from Ringschrauben
export const ringschraubenArticleIds = Object.values(Ringschrauben).map(r => r.articleNumber);
export const Gewindestangen = u<IRingschraube>().record({
  M20: { articleNumber: "00E13000468", drill: "Ø22" } // https://plaspack.atlassian.net/browse/APP-1275
});
export const gewindeStangenArticleIds = Object.values(Gewindestangen).map(r => r.articleNumber);

export const RingschraubenExtended = {
  ...Ringschrauben,
  ...Gewindestangen
}


export const crossAdapters = ["00E13001741", "00E13001742"];

export type DoubleMountType = "x" | "xs";

export type ShackleIds = "00E13001548" | "00E13000026" | "00E13000417";

export interface IRollShackleConfig {
  roll?: string;
  shackle?: ShackleIds;
  roll2?: string;
  shackle2?: ShackleIds;
}

export interface ISecurityCheckOptions {
  ignoreMinDistIn?: boolean
}

export type ProductionLocation = "AT" | "BA";

const mountCapHeight = 0.038;
const schlittenSlideOffset = 0.045;
export const HeightChangeSlideMeasures = {
  mountCapHeight,
  slideStart: mountCapHeight - 0.002,
  schlittenSlideOffset,
  minTopMount: 0.012, // https://plaspack.atlassian.net/browse/APP22-589
  distToDrill: 0.028
}


export type RemoteType = "centronic" | "centronic+";
export interface IRemoteInfo {
  articleNumber: string;
  channelCount: number;
  sunWindSensorConfigurable: boolean;
  type: RemoteType;
}

export interface IEdgeMountItemReal {
  itemType: EdgeMountItemType;
  count: number;
  dist: number;
  sideLength: number;
  concaveLength: number;
  factors: number[];
}

export const PL_AXLE_GROUP_NAME = "Welle";

export type PoleSwordType = "single" | "double";

export interface IAxleInfo {
  diameter: number;
  keder: number[];
  productId: string;
}

// https://support.soliday.eu/wellenprofile
export const axleInfos = {
  "M_30": { diameter: 30, keder: [5.5, 5.5], productId: "WP030_00Mv01" }, // M24
  "M_23": { diameter: 23, keder: [5.5], productId: "WP023_00Mv01" }, // M
  "A_50": { diameter: 50, keder: [7.5, 7.5], productId: "WP050_00Av01" }, // A old
  "One_65": { diameter: 65, keder: [5.5, 5.5], productId: "WP065_000v01" }, // A new
  "C_84": { diameter: 84, keder: [5.5, 5], productId: "WP084_00Cv01" }, // old C
  "X_84": { diameter: 84, keder: [5.5, 5.5], productId: "WP084_00Xv01" },
  "C_90": { diameter: 90, keder: [5.5, 5.5], productId: "WP090_000v01" }, // new C
  "CS_110": { diameter: 110, keder: [13], productId: "WP110_000v01" }, // old CS
  "CS_110_02": { diameter: 110, keder: [5.5, 5.5], productId: "WP110_000v02" }, // new CS
  "Flex": { diameter: 13, keder: [], productId: "SW011_000v01" }
} satisfies Record<string, IAxleInfo>;
export type AxleType = keyof typeof axleInfos;